/* ============================================================================
 * api.js — self-hosted data layer (replaces helpers/firebase.js)
 * ----------------------------------------------------------------------------
 * Three thin services that map the old Firebase usage onto the new stack:
 *   auth       -> Supabase GoTrue    (VUE_APP_AUTH_URL, default /auth/v1)
 *   rest       -> Supabase PostgREST (VUE_APP_REST_URL, default /rest/v1)
 *   functions  -> Express backend    (VUE_APP_API_URL,  default /api)
 *
 * Contract is fixed by migrated_backend/{server.js,schema.sql} + nginx.conf:
 *   forms(id uuid, patient_id, key, title, subtitle, status, data jsonb, files jsonb)
 *   patients(patient_id, project_id, initialized, study_completed)
 *   projects(id, name, active)
 * ========================================================================== */
import axios from 'axios'

const ANON_KEY = process.env.VUE_APP_SUPABASE_ANON_KEY || ''
const AUTH_URL = process.env.VUE_APP_AUTH_URL || '/auth/v1'
const REST_URL = process.env.VUE_APP_REST_URL || '/rest/v1'
const API_URL = process.env.VUE_APP_API_URL || '/api'

const SESSION_KEY = 'dbi.session' // { access_token, refresh_token, expires_at, user }

/* ----------------------------- session store ---------------------------- */
const session = {
  get() {
    try {
      return JSON.parse(localStorage.getItem(SESSION_KEY) || 'null')
    } catch (e) {
      return null
    }
  },
  set(s) {
    if (s) localStorage.setItem(SESSION_KEY, JSON.stringify(s))
    else localStorage.removeItem(SESSION_KEY)
    current = s
  },
}
let current = session.get()

function fromTokenResponse(r) {
  return {
    access_token: r.access_token,
    refresh_token: r.refresh_token,
    expires_at: Date.now() + (r.expires_in || 3600) * 1000,
    user: r.user || null,
  }
}

function mapUser(u) {
  if (!u) return null
  return {
    uid: u.id,
    email: u.email,
    displayName: (u.user_metadata && u.user_metadata.name) || null,
    role: (u.app_metadata && u.app_metadata.role) || null,
  }
}

/* ------------------------------ axios cores ----------------------------- */
const authHttp = axios.create({
  baseURL: AUTH_URL,
  headers: { apikey: ANON_KEY },
})
const restHttp = axios.create({
  baseURL: REST_URL,
  headers: { apikey: ANON_KEY },
})
const fnHttp = axios.create({ baseURL: API_URL })

// Refresh the access token if it is about to expire, then attach it.
async function ensureToken() {
  if (!current) return null
  if (current.expires_at && Date.now() < current.expires_at - 30000) {
    return current.access_token
  }
  if (!current.refresh_token) return current.access_token
  try {
    const { data } = await authHttp.post('/token?grant_type=refresh_token', {
      refresh_token: current.refresh_token,
    })
    session.set(fromTokenResponse(data))
    return current.access_token
  } catch (e) {
    return current.access_token // fall through with stale token
  }
}

// Attach a fresh bearer token to PostgREST and backend requests.
async function bearerConfig(extra = {}) {
  const token = await ensureToken()
  const headers = Object.assign({}, extra.headers)
  if (token) headers.Authorization = `Bearer ${token}`
  return Object.assign({}, extra, { headers })
}

/* ================================= AUTH ================================= */
const authListeners = []
function emitAuth() {
  const u = current ? mapUser(current.user) : null
  authListeners.forEach(cb => {
    try {
      cb(u)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }
  })
}

export const auth = {
  currentUser: () => (current ? mapUser(current.user) : null),
  isLoggedIn: () => !!current,

  async signIn(email, password) {
    const { data } = await authHttp.post('/token?grant_type=password', {
      email,
      password,
    })
    session.set(fromTokenResponse(data))
    emitAuth()
    return this.currentUser()
  },

  async signUp(email, password) {
    const { data } = await authHttp.post('/signup', { email, password })
    if (data.access_token) {
      session.set(fromTokenResponse(data))
      emitAuth()
    }
    return data.user ? mapUser(data.user) : null
  },

  async sendPasswordReset(email) {
    await authHttp.post('/recover', { email })
  },

  async signOut() {
    if (current) {
      try {
        await authHttp.post('/logout', {}, await bearerConfig())
      } catch (e) {
        /* ignore */
      }
    }
    session.set(null)
    emitAuth()
  },

  // Verify/refresh the stored session against GoTrue (used on app boot).
  async restore() {
    if (!current) return null
    await ensureToken()
    try {
      const { data } = await authHttp.get('/user', await bearerConfig())
      current.user = data
      session.set(current)
    } catch (e) {
      // Only drop the session if the token is genuinely rejected (401/403).
      // Transient network/5xx errors must NOT log the operator out.
      const status = e.response && e.response.status
      if (status === 401 || status === 403) session.set(null)
    }
    emitAuth()
    return this.currentUser()
  },

  onAuthChange(cb) {
    authListeners.push(cb)
    Promise.resolve().then(() => cb(this.currentUser()))
    return () => {
      const i = authListeners.indexOf(cb)
      if (i !== -1) authListeners.splice(i, 1)
    }
  },

  async updatePassword(password) {
    const cfg = await bearerConfig()
    const { data } = await authHttp.put('/user', { password }, cfg)
    if (current) {
      current.user = data
      session.set(current)
      emitAuth()
    }
    return data ? mapUser(data) : null
  },
}

/* =============================== POSTGREST ============================== */
async function restGet(path) {
  const cfg = await bearerConfig({ headers: { Accept: 'application/json' } })
  const { data } = await restHttp.get(path, cfg)
  return data
}
async function restPatch(path, body) {
  const cfg = await bearerConfig({
    headers: { Prefer: 'return=representation' },
  })
  const { data } = await restHttp.patch(path, body, cfg)
  return data
}

export const rest = {
  // --- projects ---
  async listProjects() {
    return restGet('/projects?select=*&order=name')
  },

  // --- patients ---
  async getPatient(patientId) {
    const rows = await restGet(
      `/patients?patient_id=eq.${encodeURIComponent(patientId)}&select=*`
    )
    return rows[0] || null
  },
  async listPatients(projectSlug) {
    return restGet(
      `/patients?project_id=eq.${encodeURIComponent(
        projectSlug
      )}&select=*&order=patient_id`
    )
  },
  async updatePatient(patientId, patch) {
    return restPatch(
      `/patients?patient_id=eq.${encodeURIComponent(patientId)}`,
      patch
    )
  },
  async listAllPatients() {
    return restGet('/patients?select=*,projects(name)&order=patient_id')
  },
  // Derived per-subject data-entry completeness (Screening + Patient Data
  // Collection; Breath Measurement '*-data' forms excluded). Read-only view,
  // independent of the manual study_completed flag. See schema.sql.
  async listPatientEntryStatus() {
    return restGet(
      '/patient_entry_status?select=patient_id,entries_complete,entry_forms_total,entry_forms_finished'
    )
  },
  async getMyOperator(email) {
    const rows = await restGet(
      `/operators?email=eq.${encodeURIComponent(
        email
      )}&select=role,active,must_change_password`
    )
    return rows[0] || null
  },
  // Active operators, for the "Operator in charge" picker. Reflects the live
  // user list (incl. anyone an admin adds later); readable by any authenticated
  // user under the operators RLS policy.
  async listOperators() {
    return restGet('/operators?active=eq.true&select=email,role&order=email')
  },
  // Clear the "temporary password" flag once the user sets their own.
  async clearMustChangePassword(operatorId) {
    return restPatch(`/operators?id=eq.${encodeURIComponent(operatorId)}`, {
      must_change_password: false,
    })
  },
  // Project ids a given operator is assigned to (used to scope the dashboard).
  async listProjectIdsForOperator(operatorId) {
    const rows = await restGet(
      `/operator_projects?operator_id=eq.${encodeURIComponent(
        operatorId
      )}&select=project_id`
    )
    return rows.map(r => r.project_id)
  },
  async listAuditForRow(table, pk) {
    return restGet(
      `/audit_log?table_name=eq.${table}&row_pk=eq.${encodeURIComponent(
        pk
      )}&select=*&order=changed_at.desc`
    )
  },
  async listAudit(limit = 200) {
    return restGet(`/audit_log?select=*&order=changed_at.desc&limit=${limit}`)
  },

  // --- drugs (medication autocomplete; replaces the old Algolia index) ---
  async searchDrugs(query, limit = 10) {
    const q = encodeURIComponent(`*${query}*`)
    const path = `/drugs?or=(name.ilike.${q},active_substance.ilike.${q})&limit=${limit}&select=*`
    return restGet(path)
  },

  // --- forms ---
  async listForms(patientId) {
    return restGet(
      `/forms?patient_id=eq.${encodeURIComponent(patientId)}&select=*`
    )
  },
  async getForm(patientId, key) {
    const rows = await restGet(
      `/forms?patient_id=eq.${encodeURIComponent(
        patientId
      )}&key=eq.${encodeURIComponent(key)}&select=*`
    )
    return rows[0] || null
  },
  async updateForm(patientId, key, patch) {
    return restPatch(
      `/forms?patient_id=eq.${encodeURIComponent(
        patientId
      )}&key=eq.${encodeURIComponent(key)}`,
      patch
    )
  },

  /**
   * Poll-based replacement for Firestore onSnapshot. Calls `fetcher` now and
   * every `intervalMs`, passing the result to `onData`. Returns an unsubscribe.
   */
  subscribe(fetcher, onData, onError, intervalMs = 4000) {
    let stopped = false
    const tick = () => {
      if (stopped) return
      Promise.resolve()
        .then(fetcher)
        .then(d => {
          if (!stopped) onData(d)
        })
        // eslint-disable-next-line no-console
        .catch(e => (onError ? onError(e) : console.error('subscribe', e)))
    }
    tick()
    const timer = setInterval(tick, intervalMs)
    return () => {
      stopped = true
      clearInterval(timer)
    }
  },
}

/* =============================== FUNCTIONS ============================== *
 * Backend accepts the plain payload (and also the legacy {data} envelope).
 * We send plain JSON and read the plain response. */
export const functions = {
  async checkIn(patientId) {
    const { data } = await fnHttp.post(
      '/checkIn',
      { patientId },
      await bearerConfig()
    )
    return data
  },
  async addForms(patientId, projectSlug, numberOfVisit) {
    const { data } = await fnHttp.post(
      '/addForms',
      { patientId, projectSlug, numberOfVisit },
      await bearerConfig()
    )
    return data
  },
  async exportData(projectSlug) {
    // The backend streams the CSV over this authenticated request; grab it as a
    // Blob so the caller can trigger a client-side download (no public URL).
    const { data } = await fnHttp.post(
      '/exportData',
      { projectSlug },
      await bearerConfig({ responseType: 'blob' })
    )
    return data // Blob (text/csv)
  },
  async sendSupport({ subject, message }) {
    // Backend emails this to the study team; the sender is the logged-in user.
    const { data } = await fnHttp.post(
      '/support',
      { subject, message },
      await bearerConfig()
    )
    return data
  },
  async correctPatient({ patientId, patch, reason }) {
    const { data: res } = await fnHttp.post(
      '/correctPatient',
      { patientId, patch, reason },
      await bearerConfig()
    )
    return res
  },
  // Permanently delete a whole study subject (patient + all its forms). The
  // backend enforces admin-only; the deletion is recorded in audit_log with the
  // supplied reason. Returns { patient_id, deleted, forms_deleted }.
  async deletePatient({ patientId, reason }) {
    const { data: res } = await fnHttp.post(
      '/deletePatient',
      { patientId, reason },
      await bearerConfig()
    )
    return res
  },
}

/* ========================== ADMIN (user mgmt) ========================== *
 * All endpoints are guarded server-side by requireAdmin; the frontend gating
 * is only for UX. Routed through the Express backend (service-role). */
export const admin = {
  async listUsers() {
    const { data } = await fnHttp.get('/admin/users', await bearerConfig())
    return (data && data.users) || []
  },
  async createUser({ email, password, role, projectIds }) {
    const { data } = await fnHttp.post(
      '/admin/users',
      { email, password, role, projectIds },
      await bearerConfig()
    )
    return data
  },
  async updateUser(id, patch) {
    const { data } = await fnHttp.patch(
      `/admin/users/${id}`,
      patch,
      await bearerConfig()
    )
    return data
  },
  async setPassword(id, password) {
    const { data } = await fnHttp.post(
      `/admin/users/${id}/password`,
      { password },
      await bearerConfig()
    )
    return data
  },
  async deleteUser(id) {
    const { data } = await fnHttp.delete(
      `/admin/users/${id}`,
      await bearerConfig()
    )
    return data
  },
}

export default { auth, rest, functions, admin }
